import React from 'react';
import { Star, CheckCircle, MessageSquare } from 'lucide-react';

const RatingBar = ({ label, value, max = 5 }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600 w-28 truncate">{label}</span>
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-sm font-medium text-gray-700 w-8 text-right">{value > 0 ? value.toFixed(1) : '—'}</span>
    </div>
  );
};

const DistributionBar = ({ stars, count, total }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-500 w-12">{stars} star</span>
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-gray-400 w-8 text-right">{count}</span>
    </div>
  );
};

const ProviderReviewsList = ({ providerId, reviewData }) => {
  if (!reviewData) return null;

  const { reviews = [], stats } = reviewData;

  return (
    <div className="space-y-6">
      {/* Aggregate Stats */}
      {stats && (
        <div className="bg-white rounded-xl border p-6">
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Overall Score */}
            <div className="text-center sm:border-r sm:pr-6">
              <div className="text-4xl font-bold text-gray-900">{stats.averageRating?.toFixed(1) || '—'}</div>
              <div className="flex items-center justify-center gap-0.5 mt-1">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star
                    key={s}
                    size={16}
                    className={s <= Math.round(stats.averageRating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
                  />
                ))}
              </div>
              <p className="text-sm text-gray-400 mt-1">{stats.totalReviews || 0} review{(stats.totalReviews || 0) !== 1 ? 's' : ''}</p>
            </div>

            {/* Breakdown */}
            <div className="flex-1 space-y-2">
              <h4 className="text-sm font-semibold text-gray-600 mb-2">Category Breakdown</h4>
              {stats.breakdown && (
                <>
                  <RatingBar label="Quality" value={stats.breakdown.quality || 0} />
                  <RatingBar label="Reliability" value={stats.breakdown.reliability || 0} />
                  <RatingBar label="Value" value={stats.breakdown.value || 0} />
                  <RatingBar label="Communication" value={stats.breakdown.communication || 0} />
                </>
              )}
            </div>

            {/* Distribution */}
            {stats.distribution && (
              <div className="sm:w-48 space-y-1.5">
                <h4 className="text-sm font-semibold text-gray-600 mb-2">Distribution</h4>
                {[5, 4, 3, 2, 1].map(s => (
                  <DistributionBar
                    key={s}
                    stars={s}
                    count={stats.distribution[String(s)] || 0}
                    total={stats.totalReviews || 0}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Review Cards */}
      {reviews.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <Star size={36} className="mx-auto mb-3 text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-600">No reviews yet</h3>
          <p className="text-gray-400 mt-1">Be the first to review this provider</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map(review => (
            <div key={review._id} className="bg-white rounded-xl border p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{review.reviewerName || 'Anonymous'}</span>
                    {review.verified && (
                      <span className="flex items-center gap-1 text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">
                        <CheckCircle size={10} /> Verified
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {review.createdAt ? new Date(review.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star
                      key={s}
                      size={14}
                      className={s <= Math.round(review.overallRating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
                    />
                  ))}
                  <span className="ml-1 text-sm font-semibold text-gray-700">{(review.overallRating || 0).toFixed(1)}</span>
                </div>
              </div>

              {/* Mini Breakdown */}
              {review.ratings && (
                <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-500">
                  <span>Quality: {review.ratings.quality}/5</span>
                  <span>Reliability: {review.ratings.reliability}/5</span>
                  <span>Value: {review.ratings.value}/5</span>
                  <span>Comms: {review.ratings.communication}/5</span>
                </div>
              )}

              {review.comment && (
                <p className="text-gray-600 mt-3">{review.comment}</p>
              )}

              {/* Provider Response */}
              {review.providerResponse && (
                <div className="mt-3 ml-4 p-3 bg-gray-50 rounded-lg border-l-3 border-[#1F3A68]">
                  <div className="flex items-center gap-1.5 mb-1">
                    <MessageSquare size={12} className="text-[#1F3A68]" />
                    <span className="text-xs font-semibold text-[#1F3A68]">Provider Response</span>
                  </div>
                  <p className="text-sm text-gray-600">{review.providerResponse}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProviderReviewsList;
